/**
 * The operator's calendar date and IANA time zone, read from the device clock.
 * The assistant needs them to resolve "today" and "tomorrow": the server and
 * the model know only UTC, which is already the next day for an evening
 * operator in the Americas.
 */
export type OperatorClock = { date: string; timeZone: string };

/**
 * How far a device clock may sit from the server's before its date is not
 * trusted: request latency and ordinary skew, never a wrong clock. The text
 * edge and the voice session use the same limit.
 */
export const CLOCK_TOLERANCE_MS = 10 * 60_000;

/** True when the server sent no time (an older backend) or the device agrees with it. */
export function deviceClockMatchesServer(
  serverTime: number | undefined,
  now: number = Date.now(),
): boolean {
  return serverTime === undefined || Math.abs(now - serverTime) <= CLOCK_TOLERANCE_MS;
}

/** The calendar date in `timeZone` at `instant` as YYYY-MM-DD; throws for an unknown zone. */
export function dateInZone(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(instant);
  const part = (type: string) => parts.find((candidate) => candidate.type === type)?.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/** Null when the runtime exposes no usable time zone; callers then send nothing. */
export function currentOperatorClock(
  now: Date = new Date(),
  deviceTimeZone: () => string | undefined = () => Intl.DateTimeFormat().resolvedOptions().timeZone,
): OperatorClock | null {
  try {
    const timeZone = deviceTimeZone();
    if (!timeZone) return null;
    return { date: dateInZone(now, timeZone), timeZone };
  } catch {
    return null;
  }
}

/** How the assistant turns the date into document dates; shared by text and voice. */
export const RELATIVE_DATE_RULE =
  "Resolve relative dates such as today, tomorrow, and yesterday from it, and send dates as YYYY-MM-DD.";

/**
 * A non-interrupting note for the live voice agent, which has no server turn to
 * carry the date. Without a clock it says so, as the text path does, so the
 * agent confirms a relative date instead of guessing one.
 */
export function operatorClockNote(clock: OperatorClock | null): string {
  if (!clock)
    return "The operator's local date could not be determined (no usable time zone, or a device clock that disagrees with the server's). Do not assume a relative date: before using today, tomorrow, yesterday, or any other relative date, ask the operator to confirm the calendar date, then send it as YYYY-MM-DD.";
  return `Today is ${clock.date} (${clock.timeZone}). ${RELATIVE_DATE_RULE}`;
}

const DATE_CHECK_INTERVAL_MS = 60_000;

/**
 * A voice session can outlive local midnight, and the agent keeps the date it
 * was last told. Calls `onChange` when the operator's calendar day moves on;
 * the returned function stops watching.
 */
export function watchOperatorDate(
  announcedDate: string | undefined,
  onChange: (clock: OperatorClock) => void,
): () => void {
  let announced = announcedDate;
  const timer = setInterval(() => {
    const clock = currentOperatorClock();
    if (!clock || clock.date === announced) return;
    announced = clock.date;
    onChange(clock);
  }, DATE_CHECK_INTERVAL_MS);
  return () => clearInterval(timer);
}
